<template>
  <v-card class="team-card" flat>
    <div class="team-card__header">
      <div>
        <v-icon class="team-card__header-icon">badge</v-icon>
        <span>Perfis</span>
      </div>
      <v-btn color="primary" depressed round small @click="openNew">
        <v-icon left small>add</v-icon> Novo perfil
      </v-btn>
    </div>

    <div v-if="loading" class="pa-4 text-xs-center">
      <v-progress-circular indeterminate color="primary"></v-progress-circular>
    </div>

    <v-layout v-else row wrap class="pa-2">
      <v-flex xs12 sm6 v-for="profile in profiles" :key="profile.id">
        <div class="profile-tile">
          <div class="profile-tile__top">
            <div>
              <span class="profile-tile__name">{{ profile.name }}</span>
              <v-chip v-if="profile.default_kind" small label class="ml-1 profile-tile__badge">
                {{ profile.default_kind === 'master' ? 'Padrão' : 'Padrão' }}
              </v-chip>
            </div>
            <div>
              <v-btn icon small @click="openEdit(profile)"><v-icon small>edit</v-icon></v-btn>
              <v-btn icon small @click="duplicate(profile)"><v-icon small>content_copy</v-icon></v-btn>
              <v-btn
                icon small
                :disabled="!!profile.default_kind || profile.operators_count > 0"
                @click="remove(profile)"
              ><v-icon small>delete</v-icon></v-btn>
            </div>
          </div>
          <div class="profile-tile__caps">
            <v-chip v-for="cap in profile.capabilities" :key="cap" x-small class="profile-tile__cap">
              {{ catalog[cap] || cap }}
            </v-chip>
            <span v-if="!profile.capabilities.length" class="caption grey--text">Sem permissões</span>
          </div>
          <div class="caption grey--text mt-1">
            {{ profile.operators_count }} operador(es)
          </div>
        </div>
      </v-flex>
    </v-layout>

    <!-- Create/Edit dialog -->
    <v-dialog v-model="dialog" persistent max-width="480px">
      <v-card class="team-modal">
        <v-card-title class="headline">
          <v-icon color="primary" class="mr-2">badge</v-icon>
          {{ editing ? 'Editar perfil' : 'Novo perfil' }}
        </v-card-title>
        <v-card-text>
          <v-text-field label="Nome do perfil" v-model="form.name" :disabled="isDefault"></v-text-field>
          <div class="subheading mt-2 mb-1">Permissões</div>
          <v-list class="pa-0">
            <v-list-tile v-for="(label, key) in catalog" :key="key" class="cap-row">
              <v-list-tile-content>
                <v-list-tile-title>{{ label }}</v-list-tile-title>
              </v-list-tile-content>
              <v-list-tile-action>
                <v-switch v-model="form.capabilities" :value="key" color="primary"></v-switch>
              </v-list-tile-action>
            </v-list-tile>
          </v-list>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn flat @click="dialog = false" :disabled="saving">Cancelar</v-btn>
          <v-btn color="primary" :loading="saving" :disabled="!form.name" @click="save">Salvar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar" :color="snackColor" :timeout="4000">{{ snackText }}</v-snackbar>
  </v-card>
</template>

<script>
import Flowit from "@/services/flowit";

export default {
  name: "ProfilesPanel",
  data() {
    return {
      loading: true,
      saving: false,
      profiles: [],
      catalog: {},
      dialog: false,
      editing: null,
      form: { name: "", capabilities: [] },
      snackbar: false,
      snackText: "",
      snackColor: "success",
    };
  },
  computed: {
    isDefault() {
      return !!(this.editing && this.editing.default_kind);
    },
  },
  created() {
    this.load();
  },
  methods: {
    notify(text, color = "success") {
      this.snackText = text;
      this.snackColor = color;
      this.snackbar = true;
    },
    load() {
      this.loading = true;
      Flowit.team
        .listProfiles()
        .then((res) => {
          this.catalog = res.data.catalog;
          this.profiles = res.data.profiles;
        })
        .catch(() => this.notify("Erro ao carregar perfis", "error"))
        .finally(() => {
          this.loading = false;
        });
    },
    openNew() {
      this.editing = null;
      this.form = { name: "", capabilities: [] };
      this.dialog = true;
    },
    openEdit(profile) {
      this.editing = profile;
      this.form = { name: profile.name, capabilities: [...profile.capabilities] };
      this.dialog = true;
    },
    save() {
      this.saving = true;
      const payload = { name: this.form.name, capabilities: this.form.capabilities };
      const req = this.editing
        ? Flowit.team.updateProfile(this.editing.id, payload)
        : Flowit.team.createProfile(payload);
      req
        .then(() => {
          this.dialog = false;
          this.notify("Perfil salvo com sucesso");
          this.load();
          this.$emit("profiles-changed");
        })
        .catch((e) => {
          const msg = (e.response && e.response.data && e.response.data.errors) || ["Erro ao salvar perfil"];
          this.notify(Array.isArray(msg) ? msg.join(", ") : msg, "error");
        })
        .finally(() => {
          this.saving = false;
        });
    },
    duplicate(profile) {
      Flowit.team
        .duplicateProfile(profile.id)
        .then(() => {
          this.notify("Perfil duplicado");
          this.load();
          this.$emit("profiles-changed");
        })
        .catch(() => this.notify("Erro ao duplicar perfil", "error"));
    },
    remove(profile) {
      if (!window.confirm(`Excluir o perfil "${profile.name}"?`)) return;
      Flowit.team
        .deleteProfile(profile.id)
        .then(() => {
          this.notify("Perfil excluído");
          this.load();
          this.$emit("profiles-changed");
        })
        .catch((e) => {
          const msg = (e.response && e.response.data && e.response.data.errors) || ["Erro ao excluir perfil"];
          this.notify(Array.isArray(msg) ? msg.join(", ") : msg, "error");
        });
    },
  },
};
</script>

<style scoped>
.team-card {
  border-radius: 18px !important;
  box-shadow: 0 6px 22px rgba(17, 24, 39, 0.06) !important;
  overflow: hidden;
  margin-bottom: 18px;
}
.team-card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  font-size: 17px;
  font-weight: 700;
  color: #1f2933;
}
.team-card__header > div:first-child {
  display: flex;
  align-items: center;
  gap: 10px;
}
.team-card__header-icon {
  color: var(--primary-color, #1976d2) !important;
}
.profile-tile {
  border: 1px solid #eceef2;
  border-radius: 14px;
  padding: 14px 16px;
  height: 100%;
}
.profile-tile__top {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.profile-tile__name {
  font-size: 15px;
  font-weight: 700;
  color: #263238;
}
.profile-tile__badge {
  background: #eef5ff !important;
  color: #1e6cff !important;
}
.profile-tile__caps {
  margin-top: 8px;
}
.profile-tile__cap {
  background: #f2f4f7 !important;
}
.team-modal {
  border-radius: 18px !important;
}
.cap-row {
  border-bottom: 1px solid #f2f4f7;
}
</style>
